// @flow
import React from 'react';
import moment from 'moment';

import IconButton from 'material-ui/IconButton';
import TextField from 'material-ui/TextField';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import ClockIcon from '@material-ui/icons/AccessTime';
import Tooltip from 'material-ui/Tooltip';

import Editable from '../Editable';
import * as Authorization from '../../../../lib/authorization';
import * as AppState from '../../../../state/reducer/app/appState';
import Console from '../../../../lib/debug';
import PrimaryButton from '../../../common/component/PrimaryButton';
import { splitDayKey } from '../../../../lib/designerUtils';

const MAX_SESSION_NOTES_LENGTH = 10000;

type Props = {
    onTitleBlur: () => void, //required
    onUpdateInstruction: () => void, //required
    onTogglePublish: (event: InputEvent) => void, //required
    onCopyAndReplace: () => void, // required
    onExport: () => void, //required
    onDelete: () => void, //required
    instruction: string,
    isFake: boolean,
    isPubEnabled: boolean,
    isSubscription: boolean,
    isProgram: boolean,
    isPublished: boolean,
    sessionId: number,
    publishDate: string,
    date: string,
    logo: string,
    title: string
};

type State = {
    notes: string
};

export default class SessionHeader extends React.Component<Props, State> {
    state = {
        notes: this.props.instruction || ''
    };

    onPublishClick = (event: InputEvent) => {
        if (!Authorization.publishSession.isAllowed()) {
            return AppState.showUnauthorizedDialog();
        }
        if (this.props.isSubscription) {
            return this.props.onCopyAndReplace();
        }

        return this.props.onTogglePublish(event);
    };

    getHeaderOptions = () => {
        if (this.props.isFake) return null;

        let {
            isPubEnabled,
            isSubscription,
            isProgram,
            isPublished,
            sessionId
        } = this.props;

        let publishText = '';
        let publishButton = null;

        if (isSubscription) {
            publishText = 'Copy and Edit';
        } else if (isPublished) {
            publishText = 'Unpublish Session';
        } else if (!isPublished && !isPubEnabled && !isProgram) {
            publishText = 'Publish Session';
        } else if (!isPublished && isPubEnabled && !isProgram) {
            publishText = 'Publish Session';
        }

        if (!isProgram) {
            publishButton = (
                <PrimaryButton
                    className="publish-button"
                    color="primary"
                    onClick={this.onPublishClick}
                >
                    {publishText}
                </PrimaryButton>
            );
        }

        return (
            <div className="session-options-wrapper">
                <div className="session-options layout-row">
                    <Tooltip title="Save Session to Library as...">
                        <IconButton
                            key="save-button"
                            onClick={this.props.onExport.bind(this)}
                        >
                            <SaveIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Session">
                        <IconButton
                            key="del-button"
                            onClick={this.props.onDelete.bind(this)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                {publishButton}
            </div>
        );
    };

    getPublishLabel = () => {
        let {
            isPublished,
            isPubEnabled,
            isSubscription,
            isProgram,
            publishDate
        } = this.props;

        if (isProgram) return null;

        let classes = 'published-tag';
        let label = null;
        let icon = <span className="spot">&bull;</span>;

        if (isSubscription) {
            label = 'Published';
        } else if (isPublished) {
            label = 'Published';
        } else if (!isPublished && !isPubEnabled) {
            label = 'Unpublished';
        } else if (!isPublished && isPubEnabled) {
            classes += ' scheduled';
            icon = <ClockIcon />;
            label = publishDate;
        }

        return (
            <span className={classes}>
                {icon} <span>{label}</span>
            </span>
        );
    };

    handleChange = (event: InputEvent) => {
        if (event.currentTarget.value.length > MAX_SESSION_NOTES_LENGTH) return;

        this.setState({
            notes: event.currentTarget.value
        });
    };

    currentDayText = () => {
        if (!this.props.isProgram) {
            let currentDate = moment(this.props.date).format('dddd MMMM D');
            return <h2 className="title date">{currentDate}</h2>;
        }

        if (!this.props.designerInfo.dayId) return null;

        let key = splitDayKey(this.props.designerInfo.dayId);

        const dayOfWeek = 'Week ' + (parseInt(key.weekIndex, 10) + 1);
        const dayName = 'Day ' + (parseInt(key.dayIndex, 10) + 1);
        const convertedDate = dayOfWeek + ' ' + dayName;
        return <h2 className="title date">{convertedDate}</h2>;
    };

    render() {
        Console.render('DesignerSessionHeader');
        let { date, logo, title, isPublished, isProgram } = this.props;

        let sessionLogo = logo ? (
            <div className="session-logo">
                <img src={logo} alt="Session Logo" />
            </div>
        ) : (
            <div className="session-logo">
                <span />
            </div>
        );

        let sessionTitle = (
            <span>
                <Editable
                    value={title}
                    onValueChanged={this.props.onTitleBlur.bind(this)}
                />
            </span>
        );

        let characterCount = (
            <span className="character-count">
                {this.state.notes.length}/{MAX_SESSION_NOTES_LENGTH}
            </span>
        );

        const placeholder =
            'Use this area to help the athlete understand goals ' +
            'for today’s session.';
        let notes = (
            <div className="session-notes type-n layout-column">
                <div className="block-contents">
                    <div className="session-instructions">
                        <span className="th-textarea-label">
                            Coach Instructions
                        </span>
                        <TextField
                            id="multiline"
                            className="th-textarea"
                            placeholder={placeholder}
                            multiline
                            rowsMax="10"
                            rows="3"
                            value={this.state.notes}
                            onChange={this.handleChange}
                            onBlur={this.props.onUpdateInstruction.bind(this)}
                            margin="normal"
                            fullWidth
                            disabled={isPublished}
                        />
                        {characterCount}
                    </div>
                </div>
            </div>
        );

        return (
            <div className="session-header">
                <div className="session-head-top">
                    {this.currentDayText()}
                    {this.getHeaderOptions()}
                </div>
                <div className="session-title">
                    {sessionLogo}
                    <div className="session-data">
                        {sessionTitle}
                        {this.getPublishLabel()}
                    </div>
                </div>
                {notes}
            </div>
        );
    }
}
